import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Alert,
} from '@mui/material'
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Autorenew as ProcessingIcon,
} from '@mui/icons-material'
import { useState } from 'react'
import type { ExtractionHistory } from '@/types/invoiceAnalyser'

interface HistoryPanelProps {
  history: ExtractionHistory[]
  onItemClick?: (item: ExtractionHistory) => void
  onDeleteItem?: (documentId: string) => void
}

const STATUS_CONFIG = {
  completed: {
    icon: <CheckIcon />,
    color: 'success' as const,
    label: 'Completed',
  },
  processing: {
    icon: <ProcessingIcon sx={{ animation: 'spin 1s linear infinite' }} />,
    color: 'info' as const,
    label: 'Processing',
  },
  pending: {
    icon: <PendingIcon />,
    color: 'default' as const,
    label: 'Pending',
  },
  failed: {
    icon: <ErrorIcon />,
    color: 'error' as const,
    label: 'Failed',
  },
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

function HistoryItem({
  item,
  onView,
  onDelete,
}: {
  item: ExtractionHistory
  onView?: () => void
  onDelete?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const status = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending

  const handleExpand = () => setExpanded(!expanded)

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Chip
          icon={status.icon}
          label={status.label}
          color={status.color}
          size="small"
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ cursor: onView ? 'pointer' : 'default', fontWeight: 500 }}
            onClick={onView}
          >
            {item.filename}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatDate(item.uploadedAt)} • {formatFileSize(item.size)}
          </Typography>
        </Box>

        <IconButton size="small" onClick={handleExpand}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>

        {item.status === 'completed' && onView && (
          <IconButton size="small" onClick={onView} title="View details">
            <VisibilityIcon fontSize="small" />
          </IconButton>
        )}

        {onDelete && (
          <IconButton size="small" onClick={onDelete} title="Delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, pl: 1 }}>
          {item.result ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ minWidth: '45%', flex: 1 }}>
                <Typography variant="caption" color="textSecondary">Supplier:</Typography>
                <Typography variant="body2">{item.result.supplier || 'N/A'}</Typography>
              </Box>
              <Box sx={{ minWidth: '45%', flex: 1 }}>
                <Typography variant="caption" color="textSecondary">Invoice #:</Typography>
                <Typography variant="body2">{item.result.invoice_number || 'N/A'}</Typography>
              </Box>
              <Box sx={{ minWidth: '45%', flex: 1 }}>
                <Typography variant="caption" color="textSecondary">Date:</Typography>
                <Typography variant="body2">{item.result.invoice_date || 'N/A'}</Typography>
              </Box>
              <Box sx={{ minWidth: '45%', flex: 1 }}>
                <Typography variant="caption" color="textSecondary">Gross:</Typography>
                <Typography variant="body2">
                  {item.result.total_gross
                    ? `${item.result.total_gross} ${item.result.currency || ''}`
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          ) : item.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {item.error}
            </Alert>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {item.status === 'pending' && 'Waiting for processing...'}
              {item.status === 'processing' && 'Currently processing...'}
              {item.status === 'failed' && 'Processing failed. No details available.'}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export function HistoryPanel({
  history,
  onItemClick,
  onDeleteItem,
}: HistoryPanelProps) {
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
  )

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            History ({sortedHistory.length})
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 400 }}>
        {sortedHistory.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No processed documents yet
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Upload an invoice to see it here
            </Typography>
          </Box>
        ) : (
          sortedHistory.map((item) => (
            <HistoryItem
              key={item.documentId}
              item={item}
              onView={item.status === 'completed' && onItemClick ? () => onItemClick(item) : undefined}
              onDelete={onDeleteItem ? () => onDeleteItem(item.documentId) : undefined}
            />
          ))
        )}
      </Box>
    </Paper>
  )
}
